import { useState } from "react";
import { initializePaddle } from "@paddle/paddle-js";
import { useSubscription } from "../../context/SubscriptionContext";
import { PLANS } from "../../features/subscription/planConfig";
import Logo from "../common/Logo";
import Modal from "../common/Modal";
import { useAuth } from "../../context/AuthContext";

// Firestore Timestamps, raw Dates, ISO strings, and numeric epochs can all
// show up in `currentPeriodEnd` depending on how the webhook wrote it, so
// normalize defensively before formatting.
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return null;

  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function FeatureRow({ children, included = true }) {
  return (
    <li className="flex items-start gap-2.5 text-sm">
      <span
        className={`mt-0.5 shrink-0 ${
          included ? "text-emerald-400" : "text-gray-600"
        }`}
      >
        {included ? "✓" : "—"}
      </span>

      <span className={included ? "text-gray-200" : "text-gray-600"}>
        {children}
      </span>
    </li>
  );
}

function PlanPrice({ plan }) {
  return (
    <div className="flex items-baseline gap-1 mb-1">
      <span className="text-3xl sm:text-4xl font-bold tracking-tight">
        {plan.priceLabel}
      </span>

      {plan.priceMonthly > 0 && (
        <span className="text-sm text-gray-400 font-medium">
          {plan.priceSuffix}
        </span>
      )}
    </div>
  );
}

export default function SubscriptionScreen({ onClose }) {
  const {
    plan,
    usage,
    remaining,
    resetDate,
    capabilities,
    scheduledToCancel,
    currentPeriodEnd,
    downgradeToFree,
  } = useSubscription();

  const [busy, setBusy] = useState(false);

  // Cancellation dialog state machine:
  // "closed" -> "confirm" -> "submitting" -> "success" | "error"
  const [cancelDialog, setCancelDialog] = useState("closed");
  const [cancelError, setCancelError] = useState(null);

  const isPremium = plan === "premium";
  const { user } = useAuth();

  const periodEndLabel = formatDate(currentPeriodEnd);
  const handleUpgrade = async () => {
  setBusy(true);

  try {
    const paddle = await initializePaddle({
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      environment: "production",
    });

    if (!paddle) {
      throw new Error("Paddle failed to initialize.");
    }

    paddle.Checkout.open({
      items: [
        {
          priceId: process.env.REACT_APP_PADDLE_PRICE_ID,
          quantity: 1,
        },
      ],

      customer: {
        email: user.email,
      },

      customData: {
        firebaseUid: user.uid,
      },
    });

    setBusy(false);

  } catch (error) {
    console.error("Failed to open Paddle Checkout:", error);
    setBusy(false);
  }
};
  // Opens the confirmation dialog instead of firing a native confirm().
  // If cancellation is already scheduled, there's nothing left to confirm —
  // just surface the existing status so the user can't trigger it twice.
  const handleDowngradeClick = () => {
    if (scheduledToCancel) return;
    setCancelError(null);
    setCancelDialog("confirm");
  };

  const confirmDowngrade = async () => {
    setCancelDialog("submitting");
    setBusy(true);

    try {
      await downgradeToFree();
      setCancelDialog("success");
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      setCancelError(
        error.message || "Something went wrong. Please try again.",
      );
      setCancelDialog("error");
    } finally {
      setBusy(false);
    }
  };

  const closeCancelDialog = () => setCancelDialog("closed");

  return (
    <div className="flex-1 min-w-0 h-full overflow-y-auto bg-gray-950 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        <div className="flex items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            <Logo size={36} className="hidden sm:block" />

            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold truncate">
                Subscription
              </h1>

              <p className="text-gray-500 text-sm mt-1">
                Manage your InsightPulse AI plan
              </p>
            </div>
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="text-sm bg-gray-900 hover:bg-gray-800 border border-gray-800 px-4 py-2 rounded-xl transition shrink-0"
            >
              Back
            </button>
          )}
        </div>

        {/* CURRENT PLAN */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-5 sm:p-6 mb-10">

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">

            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
                Current Plan
              </div>

              <div className="text-xl font-bold flex items-center gap-2 flex-wrap">
                {capabilities.name}

                {isPremium && (
                  <span className="text-[10px] bg-blue-600 px-2 py-0.5 rounded-full font-semibold">
                    PREMIUM
                  </span>
                )}

                {isPremium && scheduledToCancel && (
                  <span className="text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full font-semibold">
                    CANCELLATION SCHEDULED
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
                Usage
              </div>

              <div className="text-xl font-bold whitespace-nowrap">
                {usage.count}

                <span className="text-gray-500 font-normal text-base">
                  {" "}
                  / {capabilities.monthlyAnalysisLimit}
                </span>
              </div>

              <div className="text-xs text-gray-500 mt-0.5">
                {remaining} remaining this month
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1.5">
                Resets
              </div>

              <div className="text-sm text-gray-300 mt-1">
                {resetDate.toLocaleDateString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>

          </div>

          <div className="mt-5 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                remaining === 0 ? "bg-red-500" : "bg-blue-500"
              }`}
              style={{
                width: `${Math.min(
                  (usage.count / capabilities.monthlyAnalysisLimit) * 100,
                  100
                )}%`,
              }}
            />
          </div>

          {isPremium && scheduledToCancel && (
            <div className="mt-5 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3.5 sm:p-4 flex items-start gap-3">
              <span className="text-amber-400 text-lg leading-none mt-0.5">⚠️</span>
              <p className="text-xs sm:text-sm text-amber-200/90 leading-relaxed">
                Your Premium plan will remain active
                {periodEndLabel ? ` until ${periodEndLabel}` : " until the end of your current billing period"}.
                {" "}You'll automatically move to the Free plan after that — no further action needed.
              </p>
            </div>
          )}

          <div className="mt-5">
            {isPremium ? (
              <button
                onClick={handleDowngradeClick}
                disabled={busy || scheduledToCancel}
                className="text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {scheduledToCancel ? "Cancellation Scheduled" : "Manage Subscription"}
              </button>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={busy}
                className="text-sm bg-blue-600 hover:bg-blue-700 px-5 py-2.5 rounded-xl font-semibold transition disabled:opacity-50"
              >
                {busy ? "Opening Checkout..." : "Upgrade to Premium"}
              </button>
            )}
          </div>

        </div>

        {/* PLAN COMPARISON */}
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
          Compare Plans
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-stretch">

          {/* FREE */}
          <div
            className={`flex flex-col rounded-2xl p-6 border ${
              !isPremium
                ? "border-blue-500/60 bg-blue-950/10"
                : "border-gray-800 bg-gray-900/40"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Free</h3>

              {!isPremium && (
                <span className="text-[10px] bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full font-semibold tracking-wide">
                  CURRENT PLAN
                </span>
              )}
            </div>

            <PlanPrice plan={PLANS.free} />

            <p className="text-xs text-gray-500 mb-5">
              Good for trying InsightPulse AI out
            </p>

            <ul className="space-y-2.5 mb-6 flex-1">
              <FeatureRow>
                {PLANS.free.monthlyAnalysisLimit} AI analyses / month
              </FeatureRow>

              <FeatureRow>Dataset upload & profiling</FeatureRow>
              <FeatureRow>Automatic insights & dashboard</FeatureRow>
              <FeatureRow>Conversational analysis</FeatureRow>
              <FeatureRow>Chart, chat & report export</FeatureRow>

              <FeatureRow included={false}>
                InsightPulse branding on exports
              </FeatureRow>
            </ul>

            {isPremium ? (
              <button
                onClick={handleDowngradeClick}
                disabled={busy || scheduledToCancel}
                className="w-full text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
              >
                {scheduledToCancel ? "Cancellation Scheduled" : "Continue with Free"}
              </button>
            ) : (
              <div className="w-full text-center text-sm text-gray-500 py-2.5 mt-auto">
                Your current plan
              </div>
            )}
          </div>

          {/* PREMIUM */}
          <div
            className={`flex flex-col rounded-2xl p-6 border-2 relative overflow-hidden ${
              isPremium
                ? "border-blue-500 bg-blue-950/20"
                : "border-blue-600/70 bg-gradient-to-b from-blue-950/40 to-gray-900/40"
            }`}
          >
            {!isPremium && (
              <span className="absolute top-0 right-0 text-[10px] font-semibold tracking-wide bg-blue-600 text-white px-3 py-1 rounded-bl-lg">
                MOST POPULAR
              </span>
            )}

            <div className="flex items-center justify-between mb-3 pr-2">
              <h3 className="text-lg font-bold">Premium</h3>

              {isPremium && (
                <span className="text-[10px] bg-blue-600 px-2 py-0.5 rounded-full font-semibold tracking-wide">
                  CURRENT PLAN
                </span>
              )}
            </div>

            <PlanPrice plan={PLANS.premium} />

            <p className="text-xs text-gray-400 mb-5">
              For professionals who need more depth
            </p>

            <ul className="space-y-2.5 mb-6 flex-1">
              <FeatureRow>
                {PLANS.premium.monthlyAnalysisLimit} AI analyses / month
              </FeatureRow>

              <FeatureRow>Full conversational analytics</FeatureRow>
              <FeatureRow>Advanced & driver analysis</FeatureRow>
              <FeatureRow>Full chart & chat export</FeatureRow>
              <FeatureRow>
                Sales Analysis Reports & Executive Summaries
              </FeatureRow>
              <FeatureRow>
                Scheduled one-time PDF reports, emailed to your team
              </FeatureRow>
              <FeatureRow>
                Clean, white-label exports — no branding
              </FeatureRow>
              <FeatureRow>Larger dataset limits</FeatureRow>
            </ul>

            {isPremium ? (
              <button
                onClick={handleDowngradeClick}
                disabled={busy || scheduledToCancel}
                className="w-full text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
              >
                {scheduledToCancel ? "Cancellation Scheduled" : "Manage Subscription"}
              </button>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={busy}
                className="w-full text-sm bg-blue-600 hover:bg-blue-700 py-2.5 rounded-xl font-semibold transition disabled:opacity-50 mt-auto"
              >
                {busy
                  ? "Opening Checkout..."
                  : "Upgrade to Premium — $10/month"}
              </button>
            )}
          </div>

        </div>

      </div>

      {/* CANCELLATION CONFIRM / SUBMITTING */}
      <Modal
        open={cancelDialog === "confirm" || cancelDialog === "submitting"}
        onClose={closeCancelDialog}
        dismissible={cancelDialog !== "submitting"}
        title="Cancel Premium Subscription?"
        footer={
          <>
            <button
              onClick={closeCancelDialog}
              disabled={cancelDialog === "submitting"}
              className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium disabled:opacity-50"
            >
              Keep Premium
            </button>

            <button
              onClick={confirmDowngrade}
              disabled={cancelDialog === "submitting"}
              className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 transition text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {cancelDialog === "submitting" && (
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {cancelDialog === "submitting" ? "Cancelling..." : "Confirm Cancellation"}
            </button>
          </>
        }
      >
        <p>
          Your subscription will <span className="text-white font-semibold">not</span> be downgraded immediately.
        </p>
        <p className="mt-3">
          You'll keep full Premium access
          {periodEndLabel ? ` until ${periodEndLabel}` : " until the end of your current billing period"}.
          After that, your account will automatically move to the Free plan — usage limits will drop to the Free
          tier and exports will include InsightPulse branding again.
        </p>
        <p className="mt-3 text-gray-500 text-xs">
          You can keep using InsightPulse AI as normal in the meantime, and this can't be undone from here once
          confirmed — you'd need to resubscribe to stay on Premium.
        </p>
      </Modal>

      {/* CANCELLATION SUCCESS */}
      <Modal
        open={cancelDialog === "success"}
        onClose={closeCancelDialog}
        title="Cancellation Scheduled"
        footer={
          <button
            onClick={closeCancelDialog}
            className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition text-sm font-semibold"
          >
            Got it
          </button>
        }
      >
        <div className="flex items-start gap-3">
          <span className="text-emerald-400 text-lg leading-none mt-0.5">✓</span>
          <p>
            Cancellation scheduled successfully. Your Premium plan will remain active
            {periodEndLabel ? ` until ${periodEndLabel}` : " until the end of your current billing period"}.
            You will automatically move to the Free plan after that period ends.
          </p>
        </div>
      </Modal>

      {/* CANCELLATION ERROR */}
      <Modal
        open={cancelDialog === "error"}
        onClose={closeCancelDialog}
        title="Cancellation Failed"
        footer={
          <>
            <button
              onClick={closeCancelDialog}
              className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium"
            >
              Close
            </button>
            <button
              onClick={confirmDowngrade}
              className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition text-sm font-semibold"
            >
              Try Again
            </button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <span className="text-red-400 text-lg leading-none mt-0.5">⚠️</span>
          <p>
            We couldn't schedule your cancellation. {cancelError}
            <br />
            Your Premium subscription hasn't changed — please try again, or contact support if this keeps happening.
          </p>
        </div>
      </Modal>
    </div>
  );
}